import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DropdownButton from './Button';
import LinkRenderer from '~/components/Generic/LinkRenderer';
import { alpha } from '@theme-ui/color';

type Props = {
  dropdown: Queries.NavDropdownFragment;
  hasScrolled: boolean;
  isMenuOpen: boolean;
  setOneDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const MobileDropdown = ({ dropdown, hasScrolled, isMenuOpen, setOneDropdownOpen }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { title, links } = dropdown;
  useEffect(() => {
    if (isMenuOpen) setIsOpen(false);
  }, [isMenuOpen]);

  useEffect(() => {
    setOneDropdownOpen(isOpen);
  }, [isOpen, setOneDropdownOpen]);

  return (
    <div
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <DropdownButton
        title={title}
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        isOpen={isOpen}
      />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            sx={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'max-content',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              pb: '0.8rem',
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backdropFilter: hasScrolled ? 'saturate(180%) blur(20px)' : 'none',
                background: hasScrolled ? (t) => alpha('backgroundGrey', 0.6)(t) : '#FBFBFB',
                zIndex: -1,
                transition: 'background 0.3s ease-in-out, backdrop-filter 0.3s ease-in-out',
              },
            }}
          >
            {links?.length &&
              links.map((link) => {
                if (!link) return null;
                const collectionSlug = isContentfulProduct(link) ? link?.collection?.[0]?.slug : null;
                const { title: linkTitle, path } = getLinkInfo(link, collectionSlug);
                return linkTitle && path ? (
                  <div
                    key={linkTitle}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LinkRenderer
                      onClick={() => setIsOpen(false)}
                      title={linkTitle}
                      slug={path}
                      sx={{
                        width: '100%',
                        height: '100%',
                        textAlign: 'center',
                        color: 'text',
                        textDecoration: 'none',
                        fontSize: '1.2rem',
                        textTransform: 'uppercase',
                        fontWeight: 'medium',
                        position: 'relative',
                        py: '0.8rem',
                        px: '2.4rem',
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 'calc(100% - 4.8rem)',
                          height: '100%',
                          borderTop: '1px solid',
                          borderColor: 'UIGray5',
                        },
                      }}
                    >
                      <div sx={{}} />
                      {linkTitle}
                    </LinkRenderer>
                  </div>
                ) : null;
              })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileDropdown;

// Type declarations
type ContentfulBundle = {
  __typename: 'ContentfulBundle';
  slug: string | null;
  name: string | null;
};

type ContentfulCallToAction = {
  __typename: 'ContentfulCallToAction';
  innerLink: string | null;
  text: string | null;
};

type ContentfulProduct = {
  __typename: 'ContentfulProduct';
  slug: string | null;
  name: string | null;
};

type ContentfulKit = {
  __typename: 'ContentfulKit';
  slug: string | null;
  name: string | null;
};

type ContentfulPage = {
  __typename: 'ContentfulPage';
  id: string;
  slug: string | null;
  title: string | null;
};

type LinkType = ContentfulBundle | ContentfulKit | ContentfulProduct | ContentfulCallToAction | ContentfulPage;

// Type guard functions
function isContentfulBundle(link: LinkType): link is ContentfulBundle {
  return link.__typename === 'ContentfulBundle';
}

function isContentfulCallToAction(link: LinkType): link is ContentfulCallToAction {
  return link.__typename === 'ContentfulCallToAction';
}

function isContentfulProduct(link: LinkType): link is ContentfulProduct {
  return link.__typename === 'ContentfulProduct';
}

function isContentfulKit(link: LinkType): link is ContentfulKit {
  return link.__typename === 'ContentfulKit';
}

function isContentfulPage(link: LinkType): link is ContentfulPage {
  return link.__typename === 'ContentfulPage';
}

// Helper function to get the title and path from a link
function getLinkInfo(link: LinkType, collectionSlug?: string | null): { title: string | null; path: string | null } {
  if (isContentfulBundle(link)) return { title: link.name, path: `/collections/${link.slug}` };
  if (isContentfulCallToAction(link)) return { title: link.text, path: link.innerLink };
  if (isContentfulProduct(link)) {
    // Use "products" as default path if no collection slug is available
    const basePath = collectionSlug ? collectionSlug : 'products';
    return { title: link.name, path: `/${basePath}/${link.slug}` };
  }
  if (isContentfulKit(link)) return { title: link.name, path: `/kits/${link.slug}` };
  if (isContentfulPage(link)) return { title: link.title, path: `/${link.slug}` };
  return { title: null, path: null };
}
